import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Stack,
  InputLabel,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useDispatch } from "react-redux"; // Redux dispatch hook
import { setWeight } from "@redux/slices/scoringSlice"; // Action to dispatch
import { setPublish } from "@redux/slices/globalSlice";

const ScoreRangeComponent = ({ handleRating, item }) => {
  const dispatch = useDispatch();
  const [ranges, setRanges] = useState([
    {
      lower: item?.range?.lowerBound?.option || "0",
      upper: item?.range?.upperBound?.option || "0",
      lowerScore: item?.range?.lowerBound?.score || 2,
      upperScore: item?.range?.upperBound?.score || 3,
    },
  ]);
  const [between, setBetween] = useState(
    item?.range?.between?.map((e) => {
      return {
        lower: e?.option?.split("-")[0],
        upper: e?.option?.split("-")[1],
        score: e?.score,
      };
    }) || []
  );
  const [error, setError] = useState("");

  const validateInput = (value) => {
    if (value === "") return "";
    const regex = /^[0-9]+$/;
    return regex.test(value) ? value : "";
  };

  const validateScoreInput = (value) => {
    return !isNaN(value) && value >= 0 ? value : 0;
  };

  const handleRangeChange = (index, field, value) => {
    const updatedRanges = [...ranges];
    if (field === "lower" || field === "upper") {
      updatedRanges[index][field] = validateInput(value);
      if (
        field === "lower" &&
        parseInt(value) > parseInt(updatedRanges[index].upper)
      ) {
        updatedRanges[index].upper = value;
      }
    } else if (field === "lowerScore") {
      updatedRanges[index].lowerScore = validateScoreInput(value);
    } else if (field === "upperScore") {
      updatedRanges[index].upperScore = validateScoreInput(value);
    }
    setRanges(updatedRanges);
    dispatch(setPublish(true));
  };

  const handleBetweenChange = (id, field, value) => {
    const updatedBetween = between.map((item) =>
      item.id === id
        ? {
            ...item,
            [field]:
              field === "lower" || field === "upper"
                ? validateInput(value)
                : validateScoreInput(value),
          }
        : item
    );
    setBetween(updatedBetween);

    if (field === "upper") {
      const currentItem = updatedBetween.find((item) => item.id === id);
      const previousItem = updatedBetween.find((item) => item.id === id - 1);

      if (previousItem) {
        if (parseInt(currentItem?.upper) <= parseInt(previousItem.upper)) {
          setError(
            "Upper value cannot be less than or equal to the previous upper value."
          );
          return;
        }
      }

      if (parseInt(currentItem?.upper) <= parseInt(currentItem?.lower)) {
        setError("Upper value cannot be less than or equal to lower value.");
      } else {
        setError("");
      }

      handleRangeChange(0, "upper", value);
    }
  };

  const handleAddBetween = () => {
    setError("");
    if (parseInt(ranges[0].upper) <= parseInt(ranges[0].lower)) {
      setError("Upper value must be greater than lower value.");
      return;
    }

    setBetween([
      ...between,
      {
        id: Date.now(),
        lower: ranges[0].lower || "1",
        upper: (parseInt(ranges[0].lower || "0") + 1).toString(),
        score: 0,
      },
    ]);
  };

  const handleRemoveBetween = (id) => {
    const updatedBetween = between.filter((item) => item.id !== id);
    setBetween(updatedBetween);

    if (updatedBetween.length === 0) {
      handleRangeChange(0, "upper", ranges[0].lower);
    } else {
      const lastBetweenUpper = updatedBetween[updatedBetween.length - 1].upper;
      handleRangeChange(0, "upper", lastBetweenUpper);
    }
  };

  const handleNextBetween = () => {
    if (error) {
      return;
    }
    if (ranges[0]) {
      const lastBetween = between[between.length - 1] || {
        upper: ranges[0].upper,
      };
      const newLower = lastBetween.upper;
      const newUpper = (parseInt(newLower) + 1).toString();
      if (parseInt(newUpper) > parseInt(newLower)) {
        setBetween([
          ...between,
          {
            id: Date.now(),
            lower: newLower,
            upper: newUpper,
            score: 0,
          },
        ]);
        handleRangeChange(0, "upper", newUpper); // Update the upper bound
      } else {
        setError("Upper value must be greater than lower value.");
      }
    }
  };

  // Function to calculate the maximum score
  const calculateMaxWeight = () => {
    // Get the highest score from lowerScore, upperScore, and all between scores
    const allScores = [
      parseInt(ranges[0]?.lowerScore) || 0,
      parseInt(ranges[0]?.upperScore) || 0,
      ...between.map((item) => parseInt(item.score) || 0),
    ];

    return Math.max(...allScores);
  };

  // Dispatch action when ranges or between change
  useEffect(() => {
    if (ranges.length > 0 && item?.fieldId) {
      const range = ranges[0];
      const transformedRange = {
        range: {
          lowerBound: {
            option: range.lower,
            score: Number(range.lowerScore),
          },
          between: between.map((item) => ({
            option: `${item.lower}-${item.upper}`,
            score: Number(item.score),
          })),
          upperBound: {
            option: range.upper,
            score: Number(range.upperScore),
          },
        },
      };

      // Calculate maxWeight based on all the scores
      const maxWeight = calculateMaxWeight();

      // Dispatch the updated range with the correct maxWeight
      dispatch(
        setWeight({
          type: "field",
          fieldId: item?.fieldId,
          scoreType: "byRange",
          range: { ...transformedRange.range },
          maxWeight, // Use the calculated maxWeight
        })
      );
    }
  }, [ranges, between, item?.fieldId, dispatch]); // Dependency on ranges, between, and item.fieldId

  return (
    <Box sx={{ px: 5, py: 2, boxSizing: "border-box" }}>
      <Stack spacing={1}>
        {/* Lower Bound */}
        <Stack direction="row" alignItems="center" justifyContent="flex-start">
          <InputLabel>Lower Bound</InputLabel>
          <Tooltip title="Less than" placement="top">
            <IconButton>{"<"}</IconButton>
          </Tooltip>
          <TextField
            size="small"
            value={ranges[0]?.lower || ""}
            onChange={(e) => handleRangeChange(0, "lower", e.target.value)}
          />
          <InputLabel>Score</InputLabel>
          <TextField
            size="small"
            value={ranges[0]?.lowerScore || ""}
            onChange={(e) => handleRangeChange(0, "lowerScore", e.target.value)}
          />
        </Stack>

        {/* Between Sections */}
        {between.map((item) => (
          <Stack
            direction="row"
            position={"relative"}
            spacing={0}
            width={"100%"}
            key={item.id}
            alignItems="center"
            justifyContent="flex-end"
          >
            <InputLabel>Between</InputLabel>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <Tooltip title="Greater than or equal to" placement="top">
                  <IconButton>≥</IconButton>
                </Tooltip>

                {/* Lower Bound (Non-editable) */}
                <TextField size="small" value={item.lower} disabled />
              </div>
              <div>
                <Tooltip title="Less than" placement="top">
                  <IconButton>{"<"}</IconButton>
                </Tooltip>
                <TextField
                  size="small"
                  value={item.upper}
                  onChange={(e) =>
                    handleBetweenChange(item.id, "upper", e.target.value)
                  }
                />
              </div>
            </div>

            <InputLabel>Score</InputLabel>
            <TextField
              size="small"
              value={item.score}
              onChange={(e) =>
                handleBetweenChange(item.id, "score", e.target.value)
              }
            />
            <IconButton
              onClick={() => handleRemoveBetween(item.id)}
              style={{ position: "absolute", top: "30%", right: "-40px" }}
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
          </Stack>
        ))}

        <Box
          display="flex"
          justifyContent="flex-end"
          width={"100%"}
          paddingRight={"60px"}
        >
          <IconButton onClick={handleNextBetween}>
            <AddCircleOutlineIcon />
          </IconButton>
        </Box>

        {/* Upper Bound */}
        <Stack direction="row" alignItems="center">
          <InputLabel>Upper Bound</InputLabel>
          <Tooltip title="Greater than or equal to" placement="top">
            <IconButton>≥</IconButton>
          </Tooltip>
          <TextField size="small" value={ranges[0]?.upper || ""} disabled />
          <InputLabel>Score</InputLabel>
          <TextField
            size="small"
            value={ranges[0]?.upperScore || ""}
            onChange={(e) => handleRangeChange(0, "upperScore", e.target.value)}
          />
        </Stack>

        {error && <FormHelperText error>{error}</FormHelperText>}
      </Stack>
    </Box>
  );
};

export default ScoreRangeComponent;
